import { TypedDocumentNode, gql } from '@apollo/client';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import Dropdown, {
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import FollowButton from '@sorare/core/src/components/user/FollowButton';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  MembersListRowActions_user,
  RemoveUserFromUserGroupMutation,
  RemoveUserFromUserGroupMutationVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  padding: var(--double-unit) 0;
`;

const StyledButton = styled(Button)`
  padding: var(--intermediate-unit) var(--double-unit);
  height: unset;
  border-radius: unset !important;
  &.unfollow {
    color: var(--c-rivals-loss);
  }
  &:hover {
    background: var(--c-neutral-400) !important;
  }
  @media ${tabletAndAbove} {
    justify-content: flex-start;
  }
`;

const StyledDropdownOptionLabel = styled(DropdownOptionLabel)`
  justify-content: center;
  @media ${tabletAndAbove} {
    justify-content: flex-start;
  }
`;

const DangerOptionLabel = styled(StyledDropdownOptionLabel).attrs({
  as: ButtonBase,
})`
  color: var(--c-rivals-loss);
  &:disabled {
    opacity: 0.7;
    pointer-events: none;
  }
`;

type Props = {
  user: Nullable<MembersListRowActions_user>;
  userGroupId: string;
};

const REMOVE_USER_FROM_USER_GROUP = gql`
  mutation RemoveUserFromUserGroupMutation(
    $input: removeUserFromSo5UserGroupInput!
  ) {
    removeUserFromSo5UserGroup(input: $input) {
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  RemoveUserFromUserGroupMutation,
  RemoveUserFromUserGroupMutationVariables
>;

export const MembersListRowActions = ({ user, userGroupId }: Props) => {
  const [open, setOpen] = useState(false);
  const { currentUser } = useCurrentUserContext();
  const [removeUser, { loading }] = useMutation(REMOVE_USER_FROM_USER_GROUP, {
    refetchQueries: ['RivalsLeagueMembersListQuery'],
  });
  const isMe = user?.slug === currentUser?.slug;
  if (isMe) {
    return null;
  }
  return (
    <Dropdown
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      label={props => (
        <IconButton {...props} small color="tertiary" icon={faEllipsis} />
      )}
      onClose={() => {
        setOpen(false);
      }}
    >
      <Wrapper gap={0}>
        {user && (
          <FollowButton
            size="large"
            subscribable={user}
            initialSubscription={user.followed}
            renderButton={(buttonProps, subscription, hovered) => (
              <StyledButton
                size="large"
                {...buttonProps}
                fullWidth
                color="transparent"
                className={classNames({
                  unfollow: subscription && hovered,
                })}
              />
            )}
          />
        )}
        <DangerOptionLabel
          bold
          disabled={loading || !user}
          onClick={() => {
            removeUser({
              variables: {
                input: {
                  so5UserGroupId: userGroupId,
                  userId: user!.id,
                },
              },
            });
          }}
        >
          <FormattedMessage
            id="MembersListRowActions.removeFromLeague"
            defaultMessage="Remove from league"
          />
        </DangerOptionLabel>
        <StyledDropdownOptionLabel
          onClick={() => {
            setOpen(false);
          }}
          bold
          color="var(--c-neutral-1000)"
        >
          <FormattedMessage {...glossary.close} />
        </StyledDropdownOptionLabel>
      </Wrapper>
    </Dropdown>
  );
};

MembersListRowActions.fragments = {
  user: gql`
    fragment MembersListRowActions_user on User {
      slug
      id
      followed {
        slug
      }
    }
  ` as TypedDocumentNode<MembersListRowActions_user>,
};
